/**
 * Jun 2026 — Overdue Invoice KPI tile.
 * Count + total outstanding of unpaid bank-transfer invoices past their due
 * date, with one-click drill-down to each booking.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ReceiptText, ArrowRight, Loader2, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export function OverdueInvoicesTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyRef, setBusyRef] = useState(null);

  const load = () => {
    api.get("/admin/invoices/overdue")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const recordPayment = async (r) => {
    const amount = window.prompt(
      `Record payment for ${r.reference} (${r.customer_name || "customer"}).\nAmount received ($):`,
      Number(r.amount || 0).toFixed(2),
    );
    if (amount === null) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    const payRef = window.prompt("Bank/payment reference (optional):", "") ?? "";
    setBusyRef(r.reference);
    try {
      await api.post(`/admin/bookings/${r.reference}/mark-paid-manual`, {
        amount_paid: amt,
        payment_method: "bank_transfer",
        payment_reference: payRef || null,
        notes: "Recorded from Overdue Invoices tile",
      });
      toast.success(`${r.reference} marked paid — $${amt.toFixed(2)} recorded`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to record payment");
    } finally {
      setBusyRef(null);
    }
  };

  const count = data?.count ?? 0;
  const tone = count > 0 ? "text-rose-700" : "text-emerald-700";

  return (
    <div
      data-testid="kpi-tile-overdue-invoices"
      className="group relative rounded-3xl bg-white border border-slate-200 p-6 hover:border-rose-400 hover:shadow-lg transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <ReceiptText className="w-4 h-4 text-rose-600" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-700">Overdue invoices</div>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div data-testid="kpi-tile-overdue-count" className={`text-5xl font-display font-bold leading-none ${tone}`}>{count}</div>
          <div className="text-xs text-slate-500 mt-1.5" data-testid="kpi-tile-overdue-total">
            {count > 0
              ? `$${(data.total_outstanding || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })} outstanding past due`
              : "No unpaid invoices past their due date"}
          </div>
          {count > 0 && (
            <div className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 max-h-44 overflow-y-auto">
              {data.rows.slice(0, 6).map((r) => (
                <div
                  key={r.reference}
                  data-testid={`overdue-invoice-row-${r.reference}`}
                  className="flex items-center justify-between text-xs rounded-lg px-2 py-1.5 hover:bg-rose-50 transition-colors"
                >
                  <Link
                    to={`/admin/bookings/${r.reference}`}
                    className="font-semibold text-slate-800 truncate hover:underline"
                  >
                    {r.customer_name || r.reference}
                    <span className="ml-1.5 font-mono text-[10px] text-slate-400">{r.reference}</span>
                  </Link>
                  <span className="flex items-center gap-2 whitespace-nowrap text-slate-600">
                    ${Number(r.amount || 0).toFixed(0)} · {r.days_overdue}d overdue
                    {r.reminders_paused && <span className="text-[9px] font-bold uppercase text-amber-600">paused</span>}
                    <button
                      type="button"
                      data-testid={`overdue-invoice-record-payment-${r.reference}`}
                      disabled={busyRef === r.reference}
                      onClick={() => recordPayment(r)}
                      title="Record payment received (bank transfer)"
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[10px] font-bold hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {busyRef === r.reference
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <BadgeDollarSign className="w-3 h-3" />}
                      Record payment
                    </button>
                    <Link to={`/admin/bookings/${r.reference}`}><ArrowRight className="w-3 h-3 text-slate-400" /></Link>
                  </span>
                </div>
              ))}
              {data.rows.length > 6 && (
                <div className="text-[10px] text-slate-400 px-2">+{data.rows.length - 6} more…</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default OverdueInvoicesTile;
